import { ComponentData, Page, PageProps } from '@accedo/page';
import { NavNode } from '@accedo/spatial-nav';
import { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useErrorScreen } from '##/components/Error/ErrorScreenContext';
import tenApiService from '##/services/tenApi';
import { config } from '##/services/store/store';
import { ComponentTypes } from '##/utils/types';
import { isLoggedIn } from '##/services/authentication/Authentication';
import {
  getContinueWatchingData,
  setWatchHistoryData,
} from '##/utils/ContinueWatchingUtil';
import { getMarqueeData } from '##/services/tenApi/collections';
import {
  CONTINUE_WATCHING_ID,
  getProcessedLiveV2Response,
  insertMarqueeItems,
} from '##/utils';
import { LiveV2Response } from '##/services/tenApi/collections/types';

type HomePageRow = {
  cType: string;
  props: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
    id: string;
    children?: ComponentData[];
    navProps?: NavNode;
  };
  isFavourite?: boolean;
  genre?: 'featured' | 'new' | 'reality' | 'drama' | 'entertainment' | 'comedy';
};

const getHomePageContent = (): Promise<HomePageRow[]> =>
  tenApiService.pages.getHomePage().then(async (homePageData) => {
    const supportedTypes: string[] = Object.values(ComponentTypes);
    const marqueeData = (await getMarqueeData()) || {};

    const homePageDataWithMarqueeItems = insertMarqueeItems(
      homePageData,
      marqueeData,
    );

    const homePageRows: HomePageRow[] = homePageDataWithMarqueeItems
      .filter((section) => supportedTypes.includes(section.type))
      .map((section, sectionIdx) => {
        const isLive = section.type?.toLowerCase() === 'live';

        return {
          cType: section.type,
          props: {
            ...section,
            id: `section-${sectionIdx}-${section.type}`,
            index: sectionIdx,
            ...(isLive &&
              getProcessedLiveV2Response(
                (section as unknown) as LiveV2Response,
              )),
          },
        };
      });
    if (isLoggedIn()) {
      const [continueWatchingData, myShowsData] = await Promise.all([
        getContinueWatchingData(),
        tenApiService.pages.getMyShowData(),
      ]);
      await setWatchHistoryData();
      if (continueWatchingData) {
        const continueWatchingSectionIdx =
          continueWatchingData.insertPosition || 2;
        const { user } = config.$get();
        const watchHistoryData = {
          cType: continueWatchingData.type,
          props: {
            ...continueWatchingData,
            id: CONTINUE_WATCHING_ID,
            title: `Continue watching for ${user.firstName}`,
            index: continueWatchingSectionIdx,
          },
        };
        // put watchhistory.insertPosition back after demo
        homePageRows.splice(continueWatchingSectionIdx, 0, watchHistoryData);
      }
      const numberOfFavouriteShows = myShowsData?.items?.length;
      if (numberOfFavouriteShows) {
        const favouriteShowsSectionIdx = myShowsData.insertPosition || 5;
        config.$set({
          myShowsIds: myShowsData.items.map((myShow) => myShow.id),
        });
        const favouriteShowsData = {
          cType: myShowsData.type,
          props: {
            ...myShowsData,
            index: favouriteShowsSectionIdx,
            items: [
              ...myShowsData.items,
              // Signal to append an "Add More Shows" button to the end
              // of the carousel, to be handled by FavouritesCarouselTile
              {
                isAddMoreShowsButton: true,
                displayAsBanner: numberOfFavouriteShows < 3,
              },
            ],
            id: 'my-show-rail',
          },
        };
        homePageRows.splice(favouriteShowsSectionIdx, 0, favouriteShowsData);
      }
    }
    homePageRows.push({
      cType: ComponentTypes.BackToTopButton,
      props: { id: 'BackToTop' },
    });

    return homePageRows;
  });

export const HomePage: FunctionalComponent<PageProps> = (props) => {
  const [homePageRows, setHomePageRows] = useState<HomePageRow[]>([]);
  const { showError } = useErrorScreen();

  useEffect(() => {
    getHomePageContent()
      .then(setHomePageRows)
      .catch((error) => showError(error.internalErrorType));
  }, []);

  if (!homePageRows.length) return null;

  return <Page {...props} data={homePageRows} />;
};
